import React from "react";
import GlobalStore from "../../store/globalStore";
import { compareValues } from "../../utils/sortingUtils";
import "./StepPageHeader.scss";

// Shared title + subtitle block for every step page rendered inside
// ProfileLayout (profile, bank, demat, documents, plan, ...). Same font, left
// alignment and spacing everywhere; the first section below it always starts
// exactly $sp-8 (32px) under the subtitle.
export interface StepPageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  className?: string;
}

// Path of the last step header shown; focus moves to the heading only when it
// changes (step to step), never on the first page load.
let lastStepPath: string | null = null;

/**
 * "Step N of M" for the current page, read from the same left-nav list the
 * sidebar renders (read only; navigation rules stay in navigationOrchestrator).
 * Matched with endsWith so it works under a PUBLIC_URL base path.
 */
export function getStepProgress(pathname: string) {
  let steps = [...(GlobalStore.leftNavBarState || [])];
  if (GlobalStore.isOTCFlow) {
    steps = steps.filter((item) => item?.name !== "Personal Documents");
  }
  steps.sort(compareValues("priority"));
  const index = steps.findIndex((s) => s?.navUrl && pathname.endsWith(s.navUrl));
  if (index < 0) return null;
  return { current: index + 1, total: steps.length, name: steps[index].name };
}

const StepPageHeader: React.FC<StepPageHeaderProps> = ({
  title,
  subtitle,
  className,
}) => {
  const headingRef = React.useRef<HTMLHeadingElement>(null);
  // The sidebar filters the left nav after this header first renders (e.g. NRI
  // drops Profiler/Plan); re-render on every update so "Step N of M" matches it.
  const [, forceUpdate] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    const unsubscribe = GlobalStore.subscribeLeftNavBarState(forceUpdate);
    forceUpdate(); // catch an update made between first render and subscribing
    return unsubscribe;
  }, []);
  const pathname = window.location.pathname;
  const progress = getStepProgress(pathname);

  React.useEffect(() => {
    if (lastStepPath !== null && lastStepPath !== pathname) {
      headingRef.current?.focus();
    }
    lastStepPath = pathname;
  }, [pathname]);

  return (
    <div className={"stepPageHeader" + (className ? " " + className : "")}>
      {progress && (
        <p className="stepPageProgress">
          Step {progress.current} of {progress.total}
          <span aria-hidden="true"> · </span>
          <span className="sr-only">: </span>
          {progress.name}
        </p>
      )}
      <h1 className="stepPageTitle" ref={headingRef} tabIndex={-1}>
        {title}
      </h1>
      {subtitle && <div className="stepPageSubtitle">{subtitle}</div>}
    </div>
  );
};

export default StepPageHeader;
